import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  BookOpen,
  Brain,
  Zap,
  Trophy,
  Volume2,
  ChevronLeft,
  ChevronRight,
  Shuffle,
  RotateCcw,
  Edit,
  Trash2,
  Swords,
  Users
} from 'lucide-react';

import { getImageUrl } from '../../utils/imageHelper';
import LeaderboardModal from './LeaderboardModal';
import { ChallengeModal } from './ChallengeModal';
import apiClient from '../../helpers/apiClient';
import { flashcardService } from '../../services/flashcard.service';



interface SetDetailViewProps {
  setId: number;
  onBack: () => void;
  onEdit?: () => void;
  onQuiz?: () => void;
  onMatching?: () => void;
  from?: string;
}

export const SetDetailView: React.FC<SetDetailViewProps> = ({ setId, onBack, onEdit, onQuiz, onMatching, from }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useSelector((state: any) => state.authentication?.userInfo);
  const isStudentMode = location.pathname.startsWith('/student/flashcards');

  // Local state for set & cards — loaded directly via API so guests (no token) can view
  const [currentSet, setCurrentSet] = useState<any>(null);
  const [setCards, setSetCards] = useState<any[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [isPrivateSet, setIsPrivateSet] = useState(false);

  // Derived: can current user edit this set?
  const canEdit = !!onEdit && !!currentUser &&
    (currentUser.role === 0 || currentUser.id === currentSet?.created_by);

  // Admin (role=0) and Teacher (role=1) can see ranking buttons in Set Detail
  const isAdminOrTeacher = currentUser?.role === 0 || currentUser?.role === 1;

  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isShuffled, setIsShuffled] = useState(false);
  const [originalCards, setOriginalCards] = useState<any[]>([]); // keep deterministic reset order
  const [displayCards, setDisplayCards] = useState<any[]>([]);
  const [leaderboardModal, setLeaderboardModal] = useState<{ isOpen: boolean; gameType: 'quiz' | 'matching' | null }>({
    isOpen: false,
    gameType: null
  });
  const [challengeModal, setChallengeModal] = useState<{ isOpen: boolean; gameType: 'quiz' | 'matching' | null }>({
    isOpen: false,
    gameType: null
  });
  const [currentStudentId, setCurrentStudentId] = useState<number | null>(null);
  const [currentStudentGifts, setCurrentStudentGifts] = useState(0);
  const [challengeState, setChallengeState] = useState<{
    challengeId?: number;
    isChallenge?: boolean;
    gameType?: 'quiz' | 'matching';
  } | null>(null);
  const [loadingTTS, setLoadingTTS] = useState(false);

  useEffect(() => {
    // Get challenge state from location if passed via navigation
    const state = location.state as any;
    if (state?.isChallenge) {
      setChallengeState(state);
      console.log('[Challenge] Challenge state from navigation:', state);
    }
  }, [location]);

  useEffect(() => {
    // Load set and cards directly via API (works for guests — no token needed)
    const loadSetAndCards = async () => {
      setPageLoading(true);
      try {
        const [setData, cardsData] = await Promise.all([
          flashcardService.getSetById(setId),
          flashcardService.getAllCards(setId),
        ]);
        setCurrentSet(setData);
        setSetCards(Array.isArray(cardsData) ? cardsData : []);
      } catch (err: any) {
        console.error('[SetDetailView] Failed to load set/cards:', err);
        // Detect private set (403) vs other errors
        if (err === 'Bộ từ này ở chế độ riêng tư. Vui lòng đăng nhập để xem.') {
          setIsPrivateSet(true);
        }
      } finally {
        setPageLoading(false);
      }
    };
    loadSetAndCards();
    // Load current student info
    const loadStudentInfo = async () => {
      try {
        console.log('[Challenge] loadStudentInfo called, currentUser:', currentUser);

        let studentId = null;
        let gifts = 0;

        // Method 1: Decode JWT token first (has priority as it's always correct)
        const accessToken = localStorage.getItem('accessToken');
        if (accessToken) {
          try {
            const parts = accessToken.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              console.log('[Challenge] JWT payload:', payload);
              if (payload.studentId) {
                studentId = payload.studentId;
                console.log('[Challenge] Got studentId from JWT:', studentId);
              }
            }
          } catch (decodeError) {
            console.log('[Challenge] Failed to decode JWT:', decodeError);
          }
        }

        // Method 2: Fallback to currentUser.studentId if JWT didn't work
        if (!studentId && currentUser?.studentId) {
          studentId = currentUser.studentId;
          console.log('[Challenge] Got studentId from currentUser:', studentId);
        }

        // If we have studentId, fetch gifts
        if (studentId) {
          try {
            const studentRes = await apiClient.get(`/api/students/${studentId}`);
            if (studentRes.data) {
              gifts = studentRes.data.gift || 0;
              console.log('[Challenge] Got gifts from API:', gifts);
            }
          } catch (err) {
            console.log('[Challenge] Could not fetch student gifts:', err);
          }

          setCurrentStudentId(studentId);
          setCurrentStudentGifts(gifts);
          console.log('[Challenge] Successfully set student state:', { studentId, gifts });
        } else {
          console.log('[Challenge] Could not determine studentId from JWT or currentUser');
        }
      } catch (error) {
        console.error('[Challenge] Error loading student info:', error);
      }
    };

    loadStudentInfo();

  }, [setId, currentUser]);

  useEffect(() => {
    // Reset to original sorted order whenever data reloads
    const sorted = [...setCards].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    setOriginalCards(sorted);
    setDisplayCards(sorted);
    setIsShuffled(false);
    setCurrentCardIndex(0);
    setIsFlipped(false);
  }, [setCards]);

  const shuffleArray = (cards: any[]) => {
    const arr = [...cards];
    for (let i = arr.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleNext = () => {
    if (!displayCards.length) return;
    setIsFlipped(false);
    setCurrentCardIndex((prev) => (prev + 1) % displayCards.length);
  };

  const handlePrevious = () => {
    if (!displayCards.length) return;
    setIsFlipped(false);
    setCurrentCardIndex((prev) => (prev - 1 + displayCards.length) % displayCards.length);
  };

  const handleShuffle = () => {
    if (!originalCards.length) return;
    const shuffled = shuffleArray(originalCards);
    setDisplayCards(shuffled);
    setIsShuffled(true);
    setCurrentCardIndex(0);
    setIsFlipped(false);
  };

  const handleReset = () => {
    setDisplayCards([...originalCards]);
    setIsShuffled(false);
    setCurrentCardIndex(0);
    setIsFlipped(false);
  };

  // Helper to get full audio URL
  const getFullAudioUrl = (audioUrl: string) => {
    if (!audioUrl) return '';
    if (audioUrl.startsWith('http://') || audioUrl.startsWith('https://')) {
      return audioUrl;
    }
    // If relative path, prepend API base URL
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.tungkt.com';
    return `${apiBaseUrl}${audioUrl.startsWith('/') ? '' : '/'}${audioUrl}`;
  };

  // Play audio using TTS API with caching.
  // Fallback: Web Speech API (works for guests without token)
  const playTTSAudio = async (word: string) => {
    if (!word || loadingTTS) return;

    setLoadingTTS(true);
    try {
      // Try backend TTS API first (cached, higher quality)
      const response = await apiClient.get(`/api/flashcards/tts/${encodeURIComponent(word)}`);

      if (response.data?.success && response.data?.audio_url) {
        const fullAudioUrl = getFullAudioUrl(response.data.audio_url);
        const audio = new Audio(fullAudioUrl);
        await audio.play();
        return;
      }
    } catch (error) {
      // API failed (e.g. guest/no token) — fall through to Web Speech API below
      console.log('[TTS] API unavailable, falling back to Web Speech API:', error);
    } finally {
      setLoadingTTS(false);
    }

    // Fallback: browser native Text-to-Speech (no token needed, works for everyone)
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(word);
      utterance.lang = 'en-US';
      window.speechSynthesis.cancel(); // cancel any previous speech
      window.speechSynthesis.speak(utterance);
    } else {
      alert('Không thể tạo phát âm cho từ này trên trình duyệt này.');
    }
  };

  const handlePlayAudio = async () => {
    if (!currentCard?.front) return;

    // If card has saved audio_url, play it directly
    if (currentCard.audio_url) {
      const fullAudioUrl = getFullAudioUrl(currentCard.audio_url);
      const audio = new Audio(fullAudioUrl);
      audio.play().catch((err) => {
        console.error('Error playing saved audio:', err);
        // Fallback to TTS if saved audio fails
        playTTSAudio(currentCard.front);
      });
    } else {
      // Use TTS API to generate and cache audio
      await playTTSAudio(currentCard.front);
    }
  };

  const handleOpenChallengeModal = async (gameType: 'quiz' | 'matching') => {
    console.log('[Challenge] Button clicked - checking student info...');

    // If not loaded yet, try to load now
    if (currentStudentId === null) {
      console.log('[Challenge] currentStudentId is null, loading now...');

      let studentId = null;
      let gifts = 0;

      // Method 1: Decode JWT token first
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        try {
          const parts = accessToken.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            console.log('[Challenge] JWT payload:', payload);
            if (payload.studentId) {
              studentId = payload.studentId;
              console.log('[Challenge] Got studentId from JWT:', studentId);
            }
          }
        } catch (decodeError) {
          console.log('[Challenge] Failed to decode JWT:', decodeError);
        }
      }

      // Method 2: Fallback to currentUser.studentId
      if (!studentId && currentUser?.studentId) {
        studentId = currentUser.studentId;
        console.log('[Challenge] Got studentId from currentUser:', studentId);
      }

      // If we have studentId, fetch gifts and set state
      if (studentId) {
        try {
          const studentRes = await apiClient.get(`/api/students/${studentId}`);
          if (studentRes.data) {
            gifts = studentRes.data.gift || 0;
            console.log('[Challenge] Got gifts from API:', gifts);
          }
        } catch (err) {
          console.log('[Challenge] Could not fetch student gifts:', err);
        }

        setCurrentStudentId(studentId);
        setCurrentStudentGifts(gifts);
        console.log('[Challenge] Set student state:', { studentId, gifts });
      }
    }

    if (!currentStudentId && !currentUser?.studentId) {
      console.log('[Challenge] Still no studentId after trying all methods');
      alert('Vui lòng đăng nhập để sử dụng tính năng này');
      return;
    }

    setChallengeModal({ isOpen: true, gameType });
  };

  const handleSubmitChallenge = async (accepterId: number, accepterName: string, giftAmount: number, gameType: 'quiz' | 'matching') => {
    try {
      const response = await apiClient.post('/api/challenges', {
        challengerId: currentStudentId,
        accepterId,
        setId,
        gameType,
        giftAmount,
      });

      alert(`Lời thách đấu đã được gửi tới ${accepterName}!`);
      // Deduct gifts from current student
      setCurrentStudentGifts(Math.max(0, currentStudentGifts - giftAmount));
    } catch (error) {
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleSubmitChallengeScore = async (score: number) => {
    if (!challengeState?.challengeId || !currentStudentId) {
      console.error('Missing challenge data');
      return;
    }

    try {
      await apiClient.patch(`/api/challenges/${challengeState.challengeId}/submit-challenger-score`, {
        score,
      });

      alert('Kết quả của bạn đã được gửi. Đợi đối thủ hoàn thành bộ từ để so kết quả!');
      navigate('/student');
    } catch (error) {
      console.error('Failed to submit score:', error);
      alert('Không thể gửi kết quả');
    }
  };

  const handlePlayCardAudio = async (card: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!card?.front) return;

    // If card has saved audio_url, play it directly
    if (card.audio_url) {
      const fullAudioUrl = getFullAudioUrl(card.audio_url);
      const audio = new Audio(fullAudioUrl);
      audio.play().catch((err) => {
        console.error('Error playing saved audio:', err);
        // Fallback to TTS if saved audio fails
        playTTSAudio(card.front);
      });
    } else {
      // Use TTS API to generate and cache audio
      await playTTSAudio(card.front);
    }
  };

  const currentCard = displayCards[currentCardIndex];

  if (isPrivateSet) {
    return (
      <Container>
        <BackButton onClick={onBack}>← Quay lại</BackButton>
        <EmptyState>
          <h3>🔒 Bộ từ riêng tư</h3>
          <p>Bộ từ này ở chế độ riêng tư. Vui lòng đăng nhập để xem.</p>
        </EmptyState>
      </Container>
    );
  }

  if (pageLoading) {
    return (
      <Container>
        <BackButton onClick={onBack}>← Quay lại</BackButton>
        <EmptyState><h3>Đang tải...</h3></EmptyState>
      </Container>
    );
  }

  if (!currentSet) {
    return (
      <Container>
        <BackButton onClick={onBack}>← Quay lại</BackButton>
        <EmptyState>
          <h3>Không tìm thấy set</h3>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <BackButton onClick={onBack}>← Quay lại</BackButton>
        </div>
        <SetInfo>
          <SetTitle>{currentSet.name}</SetTitle>
          <SetMeta>
            📇 {displayCards.length} thẻ • {currentSet.language_from} → {currentSet.language_to}
          </SetMeta>
          {currentSet.description && <SetDescription>{currentSet.description}</SetDescription>}
        </SetInfo>
      </Header>

      {/* Learning Mode Options */}
      <LearningModes>
        <ModeButton $active={true}>
          <BookOpen size={20} />
          <span>Flashcards</span>
        </ModeButton>
        <ModeButtonWithRanking onClick={onQuiz}>
          <ModeButtonContent>
            <Zap size={16} color="#f59e0b" fill="#f59e0b" />
            <span>Trắc Nghiệm</span>
          </ModeButtonContent>
          {isAdminOrTeacher && (
            <ButtonActionsGroup>
              <RankingButton onClick={(e) => { e.stopPropagation(); setLeaderboardModal({ isOpen: true, gameType: 'quiz' }); }}>
                <span>Xếp hạng</span>
              </RankingButton>
            </ButtonActionsGroup>
          )}
        </ModeButtonWithRanking>
        <ModeButtonWithRanking onClick={onMatching}>
          <ModeButtonContent>
            <Trophy size={16} color="#f59e0b" fill="#f59e0b" />
            <span>Ghép Thẻ</span>
          </ModeButtonContent>
          {isAdminOrTeacher && (
            <ButtonActionsGroup>
              <RankingButton onClick={(e) => { e.stopPropagation(); setLeaderboardModal({ isOpen: true, gameType: 'matching' }); }}>
                <span>Xếp hạng</span>
              </RankingButton>
            </ButtonActionsGroup>
          )}
        </ModeButtonWithRanking>

        {/* Game nhóm realtime — chỉ hiện với Admin/Teacher */}
        {isAdminOrTeacher && (
          <GameGroupButton onClick={() => navigate(`/game/host/${setId}`)}>
            <Users size={16} color="#fff" />
            <span>Chơi Game Nhóm</span>
          </GameGroupButton>
        )}
      </LearningModes>

      {/* Main Flashcard */}
      {displayCards.length > 0 && currentCard ? (
        <FlashcardSection>
          <ProgressBar>
            <ProgressFill $progress={(currentCardIndex + 1) / displayCards.length * 100} />
          </ProgressBar>

          <FlashcardControls>
            <ControlButton onClick={handlePrevious} disabled={displayCards.length <= 1}>
              <ChevronLeft size={24} />
            </ControlButton>

            <FlashcardWrapper onClick={handleFlip}>
              <Flashcard $isFlipped={isFlipped}>
                <FlashcardFront>
                  <CardContent>
                    <Term>{currentCard.front}</Term>
                    <Phonetic>
                      {currentCard.phonetic || ''}
                      <AudioButton onClick={(e) => { e.stopPropagation(); handlePlayAudio(); }}>
                        <Volume2 size={18} />
                      </AudioButton>
                    </Phonetic>
                  </CardContent>
                  <FlipHint>Click để lật thẻ</FlipHint>
                </FlashcardFront>

                <FlashcardBack>
                  <BackContentWrapper $hasImage={!!currentCard.image_url}>
                    <BackTextContent $hasImage={!!currentCard.image_url}>
                      <Definition $hasImage={!!currentCard.image_url}>{currentCard.back}</Definition>
                      {currentCard.example && (
                        <Example $hasImage={!!currentCard.image_url}>
                          <em>{currentCard.example}</em>
                        </Example>
                      )}
                    </BackTextContent>
                    {currentCard.image_url && (
                      <BackImageContent>
                        <CardImage src={getImageUrl(currentCard.image_url)} alt={currentCard.front} />
                      </BackImageContent>
                    )}
                  </BackContentWrapper>
                  <FlipHint>Click để lật lại</FlipHint>
                </FlashcardBack>
              </Flashcard>
            </FlashcardWrapper>

            <ControlButton onClick={handleNext} disabled={displayCards.length <= 1}>
              <ChevronRight size={24} />
            </ControlButton>
          </FlashcardControls>

          <CardCounter>
            {currentCardIndex + 1} / {displayCards.length}
          </CardCounter>

          <ActionButtons>
            <ActionButton onClick={handleShuffle}>
              <Shuffle size={18} /> Xáo trộn
            </ActionButton>
            {isShuffled && (
              <ActionButton onClick={handleReset}>
                <RotateCcw size={18} /> Đặt lại
              </ActionButton>
            )}
          </ActionButtons>
        </FlashcardSection>
      ) : (
        <EmptyState>
          <h3>Chưa có thẻ nào</h3>
          <p>Hãy thêm thẻ vào set này để bắt đầu học</p>
        </EmptyState>
      )}

      {/* Cards List */}
      <CardsListSection>
        <SectionHeader>
          <SectionTitle>📚 Tất cả thẻ ({displayCards.length})</SectionTitle>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canEdit && (
              <EditButton onClick={onEdit!}>
                <Edit size={16} /> Chỉnh sửa set
              </EditButton>
            )}
          </div>
        </SectionHeader>

        <CardsList>
          {displayCards.map((card: any, index: number) => (
            <CardListItem
              key={card.id}
              $isActive={index === currentCardIndex}
              onClick={() => {
                setCurrentCardIndex(index);
                setIsFlipped(false);
              }}
            >
              <CardListNumber>{index + 1}</CardListNumber>
              <CardListContent>
                <CardListTermRow>
                  <div>
                    <CardListTerm>{card.front}</CardListTerm>
                    {card.phonetic && (
                      <CardListPhonetic>{card.phonetic}</CardListPhonetic>
                    )}
                  </div>
                </CardListTermRow>
                <CardListDef>{card.back}</CardListDef>
              </CardListContent>
              <CardListAudioButton onClick={(e) => handlePlayCardAudio(card, e)}>
                <Volume2 size={18} />
              </CardListAudioButton>
            </CardListItem>
          ))}
        </CardsList>
      </CardsListSection>

      {/* Leaderboard Modal */}
      {leaderboardModal.gameType && (
        <LeaderboardModal
          setId={setId}
          gameType={leaderboardModal.gameType}
          isOpen={leaderboardModal.isOpen}
          onClose={() => setLeaderboardModal({ isOpen: false, gameType: null })}
          title={leaderboardModal.gameType === 'quiz' ? '🏆 Xếp Hạng Trắc Nghiệm' : '🏆 Xếp Hạng Ghép Thẻ'}
          totalCards={setCards.length}
        />
      )}

      {/* Challenge Modal */}
      {currentStudentId && (
        <ChallengeModal
          isOpen={challengeModal.isOpen}
          onClose={() => setChallengeModal({ isOpen: false, gameType: null })}
          onSubmit={handleSubmitChallenge}
          gameType={challengeModal.gameType || 'quiz'}
          currentStudentId={currentStudentId}
          currentStudentGifts={currentStudentGifts}
        />
      )}
    </Container>
  );
};

// Animations
const flipAnimation = keyframes`
  from {
    transform: rotateY(0deg);
  }
  to {
    transform: rotateY(180deg);
  }
`;

const pulseAnimation = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  color: #4b5563;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #1f2937;
  }
`;

const SetInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SetTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
`;

const SetDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
`;

const SetMeta = styled.div`
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
`;

const LearningModes = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  gap: 8px;
  margin-bottom: 20px;
  padding: 12px;
  background: white;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(70px, 1fr));
    gap: 6px;
    padding: 8px;
    width: 100%;
    overflow: hidden;
  }
`;

const ModeButton = styled.button<{ $active?: boolean }>`
  flex: 1;
  padding: 10px;
  background: ${props => props.$active ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'white'};
  color: ${props => props.$active ? 'white' : '#6b7280'};
  border: 1.5px solid ${props => props.$active ? '#3b82f6' : '#e5e7eb'};
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  transition: all 0.25s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
    border-color: ${props => props.$active ? '#2563eb' : '#3b82f6'};
  }

  svg {
    width: 18px;
    height: 18px;
  }

  span {
    font-size: 12px;
  }

  @media (max-width: 768px) {
    padding: 8px;
    font-size: 11px;

    svg {
      width: 16px;
      height: 16px;
    }

    span {
      font-size: 10px;
    }
  }
`;

const ModeButtonWithRanking = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px;
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.25s;
  min-height: auto;
  user-select: none;
  flex: 1;
  min-width: 0;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
    border-color: #f59e0b;
  }

  &:active {
    transform: translateY(0px);
  }

  @media (max-width: 768px) {
    padding: 6px;
    gap: 6px;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
  }
`;

const GameGroupButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 8px;
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s;
  flex: 1;
  min-width: 0;

  span {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.4);
  }

  @media (max-width: 768px) {
    padding: 8px 6px;
    span { font-size: 10px; }
  }
`;

const ModeButtonContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  justify-content: flex-start;
  flex: 1;
  min-width: 0;

  svg {
    width: 16px;
    height: 16px;
    color: #f59e0b;
    flex-shrink: 0;
  }

  span {
    font-size: 12px;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 768px) {
    gap: 2px;
    flex: 1;

    svg {
      width: 14px;
      height: 14px;
    }

    span {
      font-size: 10px;
    }
  }
`;

const ButtonActionsGroup = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 6px;
  }
`;

const RankingButton = styled.button`
  padding: 3px 6px;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #78350f;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  font-size: 10px;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 6px rgba(251, 191, 36, 0.3);
  }

  @media (max-width: 768px) {
    padding: 2px 4px;
    font-size: 9px;
  }
`;

const ChallengeButton = styled.button`
  padding: 3px 6px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: 1px solid #10b981;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  font-size: 10px;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3);
  }

  @media (max-width: 768px) {
    padding: 2px 4px;
    font-size: 9px;
  }
`;

const RankingIcon = styled.span`
  font-size: 12px;
  line-height: 1;
`;


const FlashcardSection = styled.div`
  margin-bottom: 32px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 18px;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  width: ${props => props.$progress}%;
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
  transition: width 0.3s ease;
`;

const FlashcardControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 48px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    gap: 32px;
    margin-bottom: 16px;
    width: 100%;
    max-width: 95vw;
    justify-content: space-between;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    gap: 0;
    margin-bottom: 12px;
    padding: 0 5px;
  }
`;

const ControlButton = styled.button`
  width: 60px;
  height: 60px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 50%;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #3b82f6;
    color: #3b82f6;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 54px;
    height: 54px;
    border-width: 2px;
  }

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
  }
`;

const FlashcardWrapper = styled.div`
  perspective: 1000px;
  cursor: pointer;
  width: 720px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    width: 600px;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 95vw;
  }
`;

const Flashcard = styled.div<{ $isFlipped: boolean }>`
  width: 100%;
  height: 460px;
  position: relative;
  transform-style: preserve-3d;
  transition: transform 0.6s;
  transform: ${props => props.$isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'};

  @media (max-width: 1024px) {
    height: 400px;
  }

  @media (max-width: 768px) {
    height: 350px;
  }

  @media (max-width: 480px) {
    height: 300px;
  }
`;

const FlashcardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  background: white;
  border: 3px solid #e5e7eb;
  border-radius: 22px;
  padding: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 25px 70px rgba(59, 130, 246, 0.25);
    border-color: #3b82f6;
  }

  @media (max-width: 768px) {
    padding: 24px;
    border-radius: 16px;
    border-width: 2px;
  }

  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 12px;
  }
`;

const FlashcardFront = styled(FlashcardFace)`
  background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%);
`;

const FlashcardBack = styled(FlashcardFace)`
  transform: rotateY(180deg);
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const BackContentWrapper = styled.div<{ $hasImage: boolean }>`
  flex: 1;
  display: flex;
  width: 100%;
  gap: 24px;
  align-items: center;
  justify-content: ${props => props.$hasImage ? 'space-between' : 'center'};

  @media (max-width: 768px) {
    gap: 16px;
    flex-direction: ${props => props.$hasImage ? 'column' : 'row'};
  }

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const BackTextContent = styled.div<{ $hasImage: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.$hasImage ? 'flex-start' : 'center'};
  justify-content: center;
  width: ${props => props.$hasImage ? '33.33%' : '100%'};

  @media (max-width: 768px) {
    width: 100%;
    align-items: center;
  }
`;

const BackImageContent = styled.div`
  width: 66.67%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 100%;
    max-height: 200px;
  }

  @media (max-width: 480px) {
    max-height: 150px;
  }
`;

const Term = styled.div`
  font-size: 60px;
  font-weight: 700;
  color: #0f172a;
  text-align: center;
  margin-bottom: 18px;

  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    margin-bottom: 8px;
  }
`;

const Phonetic = styled.div`
  font-size: 25px;
  color: #6b7280;
  font-style: italic;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-top: 4px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const AudioButton = styled.button`
  background: #eff6ff;
  border: none;
  padding: 6px;
  border-radius: 50%;
  color: #3b82f6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #3b82f6;
    color: white;
    transform: scale(1.1);
  }
`;

const CardImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 12px;

  @media (max-width: 768px) {
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    border-radius: 6px;
  }
`;

const Definition = styled.div<{ $hasImage?: boolean }>`
  font-size: ${props => props.$hasImage ? '42px' : '60px'};
  font-weight: 700;
  color: #0f172a;
  text-align: ${props => props.$hasImage ? 'left' : 'center'};
  margin-bottom: 18px;

  @media (max-width: 768px) {
    font-size: ${props => props.$hasImage ? '28px' : '36px'};
    margin-bottom: 12px;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: ${props => props.$hasImage ? '20px' : '28px'};
    margin-bottom: 8px;
  }
`;

const Example = styled.div<{ $hasImage?: boolean }>`
  font-size: ${props => props.$hasImage ? '16px' : '20px'};
  color: #4b5563;
  text-align: ${props => props.$hasImage ? 'left' : 'center'};
  padding: 12px 20px;
  background: white;
  border-radius: 8px;
  margin-top: 16px;

  @media (max-width: 768px) {
    font-size: ${props => props.$hasImage ? '14px' : '16px'};
    padding: 8px 12px;
    margin-top: 8px;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    padding: 6px 8px;
  }
`;

const FlipHint = styled.div`
  font-size: 13px;
  color: #9ca3af;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  width: 100%;
  text-align: center;
`;

const CardCounter = styled.div`
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 12px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #3b82f6;
    color: #3b82f6;
  }
`;

const CardsListSection = styled.div`
  margin-top: 40px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
`;

const EditButton = styled.button`
  padding: 8px 16px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #3b82f6;
    color: #3b82f6;
  }
`;

const CardsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    padding: 0 12px;
    gap: 10px;
  }
`;

const CardListItem = styled.div<{ $isActive: boolean }>`
  padding: 16px;
  background: white;
  border: 2px solid ${props => props.$isActive ? '#3b82f6' : '#e5e7eb'};
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  gap: 12px;
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const CardListNumber = styled.div`
  width: 32px;
  height: 32px;
  background: #eff6ff;
  color: #3b82f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
`;

const CardListContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardListTermRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const CardListTerm = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardListPhonetic = styled.div`
  font-size: 13px;
  color: #6b7280;
  font-style: italic;
  margin-top: 2px;
`;

const CardListDef = styled.div`
  font-size: 14px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardListAudioButton = styled.button`
  background: #eff6ff;
  border: none;
  padding: 8px;
  border-radius: 50%;
  color: #3b82f6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
  width: 36px;
  height: 36px;

  &:hover {
    background: #3b82f6;
    color: white;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 8px 0;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

const LeaderboardBadgesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
  padding: 16px;
  background: white;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 12px;
  }
`;

const LeaderboardBadge = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: #fbbf24;
    background: linear-gradient(135deg, #fef3c7 0%, #fef9e7 100%);
    box-shadow: 0 8px 16px rgba(251, 191, 36, 0.15);
  }

  &:active {
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const BadgeEmoji = styled.div`
  font-size: 40px;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const BadgeText = styled.div`
  font-weight: 600;
  color: #374151;
  font-size: 14px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

